import React, { useEffect, useState } from "react";
import { LayoutDashboard, Package, ShoppingBag, Truck, Store, LogOut, Users, UserCircle, KeyRound, Monitor, BarChart3, Facebook, Printer, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useOrderAlerts } from "../../hooks/useOrderAlerts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import AdminOverview from "./AdminOverview";
import AdminProducts from "./AdminProducts";
import AdminOrders from "./AdminOrders";
import AdminDrivers from "./AdminDrivers";
import AdminPOS from "./AdminPOS";
import AdminStaff from "./AdminStaff";
import AdminCustomers from "./AdminCustomers";
import AdminReports from "./AdminReports";
import AdminFacebook from "./AdminFacebook";
import AdminPrinterSetup from "./AdminPrinterSetup";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { Button } from "../../components/ui/button";

const ROLE_LABEL = {
  admin: "Super Admin",
  manager: "Manager",
  cashier: "Cashier",
  driver: "Driver",
};

const ROLE_BADGE_COLOR = {
  admin: "bg-[#C62828] text-white",
  manager: "bg-[#2F5233] text-white",
  cashier: "bg-[#E67E22] text-white",
  driver: "bg-[#1565C0] text-white",
};

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const isManagement = user?.role === "admin" || user?.role === "manager";
  const isCashier = user?.role === "cashier";
  const isAdmin = user?.role === "admin";

  // Default tab: cashiers jump straight to POS. Honors ?tab=printer (or any
  // other tab name) so a QR-scanned URL lands directly on the right tab.
  const initialTab = (() => {
    try {
      const p = new URLSearchParams(window.location.search).get("tab");
      if (p) return p;
    } catch { /* SSR */ }
    return isCashier ? "pos" : "overview";
  })();
  const [tab, setTab] = useState(initialTab);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [pwOpen, setPwOpen] = useState(false);

  // Allow child components (e.g. AdminPOS reminder banner) to switch tabs
  // without prop-drilling: window.dispatchEvent(new CustomEvent("arbe:switch-tab", {detail:"printer"}))
  useEffect(() => {
    const handler = (e) => { if (e?.detail) setTab(e.detail); };
    window.addEventListener("arbe:switch-tab", handler);
    return () => window.removeEventListener("arbe:switch-tab", handler);
  }, []);

  useEffect(() => {
    if (!isManagement) return;
    let mounted = true;
    const load = () =>
      api.get("/dashboard/low-stock").then((r) => { if (mounted) setLowStockCount(r.data.count); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, [isManagement]);

  // Live order alerts — beep + toast when a new customer order arrives
  useOrderAlerts({ enabled: !!user });

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6 min-h-[4rem] px-1 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/assets/arbe-logo.png" alt="Arbe" className="h-9 w-9 sm:h-10 sm:w-10 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <div className="overline text-[10px] sm:text-xs">Admin Console</div>
              <div className="font-heading font-extrabold text-base sm:text-xl md:text-2xl text-[#1C2826] tracking-tight truncate">ARBE MINIMART</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm flex-shrink-0">
            <span className="hidden md:inline text-[#4A5D5A]">Signed in as <span className="font-semibold text-[#1C2826]">{user?.name}</span></span>
            {user?.role && (
              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE_COLOR[user.role] || "bg-gray-400 text-white"}`} data-testid="admin-role-badge">
                {ROLE_LABEL[user.role] || user.role}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => window.open("/shop", "_blank", "noopener,noreferrer")} data-testid="open-storefront-btn" title="Open the customer-facing storefront in a new tab — see prices, banners and Add-to-Cart exactly as customers do">
              <ExternalLink className="h-4 w-4 sm:mr-1" /> <span className="hidden md:inline">View Storefront</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => nav("/admin/orders-display")} data-testid="open-orders-display" title="Open fulfilment display">
              <Monitor className="h-4 w-4 sm:mr-1" /> <span className="hidden md:inline">Orders Display</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)} data-testid="admin-change-password" title="Change password">
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout">
              <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="overflow-x-auto scrollbar-none -mx-3 sm:mx-0 px-3 sm:px-0">
            <TabsList className="bg-white border border-[#E1E5DF] p-1 rounded-xl inline-flex w-max min-w-full sm:flex-wrap sm:w-auto gap-1">
              {isManagement && (
                <TabsTrigger value="overview" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-overview">
                  <LayoutDashboard className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Overview</span>
                </TabsTrigger>
              )}
              {isManagement && (
                <TabsTrigger value="products" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 relative whitespace-nowrap" data-testid="tab-products">
                  <Package className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Products</span>
                  {lowStockCount > 0 && (
                    <span className="ml-1 sm:ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-[#E74C3C] text-white text-[10px] font-extrabold" data-testid="low-stock-badge">
                      {lowStockCount}
                    </span>
                  )}
                </TabsTrigger>
              )}
              {isManagement && (
                <TabsTrigger value="orders" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-orders">
                  <ShoppingBag className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Orders</span>
                </TabsTrigger>
              )}
              {isManagement && (
                <TabsTrigger value="drivers" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-drivers">
                  <Truck className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Drivers</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="pos" className="data-[state=active]:bg-[#E67E22] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-pos">
                <Store className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">POS</span>
              </TabsTrigger>
              {isManagement && (
                <TabsTrigger value="customers" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-customers">
                  <UserCircle className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Customers</span>
                </TabsTrigger>
              )}
              {isManagement && (
                <TabsTrigger value="reports" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-reports">
                  <BarChart3 className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Reports</span>
                </TabsTrigger>
              )}
              {isManagement && (
                <TabsTrigger value="facebook" className="data-[state=active]:bg-[#1877F2] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-facebook">
                  <Facebook className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Facebook</span>
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="staff" className="data-[state=active]:bg-[#2F5233] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-staff">
                  <Users className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Staff</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="printer" className="data-[state=active]:bg-[#1C2826] data-[state=active]:text-white rounded-lg px-3 sm:px-4 whitespace-nowrap" data-testid="tab-printer">
                <Printer className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">OC-T80A Setup</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {isManagement && <TabsContent value="overview" className="mt-6"><AdminOverview /></TabsContent>}
          {isManagement && <TabsContent value="products" className="mt-6"><AdminProducts /></TabsContent>}
          {isManagement && <TabsContent value="orders" className="mt-6"><AdminOrders /></TabsContent>}
          {isManagement && <TabsContent value="drivers" className="mt-6"><AdminDrivers /></TabsContent>}
          <TabsContent value="pos" className="mt-6"><AdminPOS /></TabsContent>
          {isManagement && <TabsContent value="customers" className="mt-6"><AdminCustomers /></TabsContent>}
          {isManagement && <TabsContent value="reports" className="mt-6"><AdminReports /></TabsContent>}
          {isManagement && <TabsContent value="facebook" className="mt-6"><AdminFacebook /></TabsContent>}
          {isAdmin && <TabsContent value="staff" className="mt-6"><AdminStaff /></TabsContent>}
          <TabsContent value="printer" className="mt-6"><AdminPrinterSetup /></TabsContent>
        </Tabs>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
